import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Eye, Lock } from 'lucide-react';

/**
 * WhatWeSeePanel — the calm two-column reassurance panel that lives at
 * the top of the "Your Privacy" tab. Copy locked with founder.
 *
 * Designed to be the single screen a member will forward to their
 * spouse or advisor when asked "is this safe?"
 */
const CAN_SEE = [
  'Your first name, last name, and username — to greet you and label your documents.',
  'Your email address and country — needed to sign you in and comply with local laws.',
  'Your password (stored scrambled — even our engineers cannot read it) and whether you have two-factor authentication turned on.',
  'Basic account activity — when you logged in, from which device. Used only for security alerts.',
  'Payment status — whether your subscription is active. Card details are handled by our Payment Gateway and Merchant of Record; we never see them.',
];

const ONLY_YOU_SEE = [
  'Every investment name in your Continuity System (e.g. shares, property, private equity).',
  'Every amount, valuation, mortgage balance, interest rate, and price.',
  'Every document you upload.',
  'The identity of your Trusted Contacts and the priority order you placed them in.',
  'Your net worth, your holdings mix, your returns.',
  'Your notes and personal reasoning on each investment.',
  'Anything you type into search or forms after login.',
];

const Column = ({ testid, icon: Icon, title, items, tone }) => (
  <Card data-testid={testid} className={`border ${tone.border}`}>
    <CardContent className="p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${tone.iconBg}`}>
          <Icon className={`w-4 h-4 ${tone.iconColor}`} aria-hidden="true" />
        </div>
        <h3 className={`text-base font-serif font-bold ${tone.title}`}>{title}</h3>
      </div>
      <ul className="space-y-2.5">
        {items.map((line) => (
          <li key={line} className="text-sm text-slate-700 leading-relaxed flex gap-2">
            <span className={`mt-1.5 w-1 h-1 rounded-full ${tone.dot} flex-shrink-0`} />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

const WhatWeSeePanel = () => (
  <div data-testid="what-we-see-panel">
    <div className="grid gap-4 md:grid-cols-2">
      <Column
        testid="what-we-can-see-column"
        icon={Eye}
        title="What MyInvestFolio can see"
        items={CAN_SEE}
        tone={{
          border: 'border-slate-200',
          iconBg: 'bg-slate-100',
          iconColor: 'text-slate-600',
          title: 'text-slate-900',
          dot: 'bg-slate-400',
        }}
      />
      <Column
        testid="what-only-you-see-column"
        icon={Lock}
        title="What only you can see"
        items={ONLY_YOU_SEE}
        tone={{
          border: 'border-emerald-200',
          iconBg: 'bg-emerald-100',
          iconColor: 'text-emerald-700',
          title: 'text-emerald-900',
          dot: 'bg-emerald-500',
        }}
      />
    </div>
    <p className="text-xs text-slate-500 italic mt-4 text-center">
      Everything under <span className="font-medium">&ldquo;What only you can see&rdquo;</span> is stored scrambled. Even if someone broke into our database, they would find only unreadable code. Only your password can unscramble it &mdash; and we don&apos;t have your password.
    </p>
    <p
      className="text-xs text-slate-500 mt-3 text-center max-w-3xl mx-auto"
      data-testid="trusted-contact-verification-note"
    >
      <span className="font-semibold text-slate-700">About your Trusted Contacts:</span>{' '}
      only after our 90-day check-in cycle confirms you are unable to act, we verify each Trusted Contact&apos;s identity through Sumsub or Smile ID before they can see anything. This step exists so no one can impersonate the person you trusted. We do not verify you this way — only them, and only then.
    </p>
  </div>
);

export default WhatWeSeePanel;
